import React, { useState, useEffect, useCallback } from "react";
import { Plus, Sun } from "lucide-react";
import { AddSunbedModal } from "../../components/sunbeds/AddSunbedModal";
import { DeleteConfirmationModal } from "../../components/sunbeds/DeleteConfirmationModal";
import { Button } from "../../components/ui/Button";

import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { SunbedsTable, SunbedRoom } from "../../components/sunbeds/SunbedsTable";
import { 
  createSunbedRoom, 
  getSunbedRooms, 
  updateSunbedRoom, 
  deleteSunbedRoom 
} from "../../services/sunbeds";

const SunbedsPage: React.FC = () => {
  const [rooms, setRooms] = useState<SunbedRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<SunbedRoom | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch Rooms from API
  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSunbedRooms();
      if (response && response.data) {
        // Map API response to UI model
        const mappedRooms: SunbedRoom[] = response.data.map((item: any) => ({
          id: item.id.toString(),
          label: item.label_formatted || item.label,
          status: (item.availability_formatted || item.availability) as "Available" | "In-session" | "Maintenance" | "Cleaning",
          totalDuration: item.total_hours_formatted || "0 mins"
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed rooms", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const filteredRooms = rooms.filter((r) =>
    r.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedRooms = filteredRooms.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleAdd = () => {
    setModalMode("add");
    setEditingRoom(null);
    setIsModalOpen(true);
  };

  const handleEdit = (room: SunbedRoom) => {
    setModalMode("edit");
    setEditingRoom(room);
    setIsModalOpen(true);
  };

  const handleSave = async (label: string) => {
    try {
      if (modalMode === "add") {
        const response = await createSunbedRoom({ label });
        if (response.success || response.message) {
            fetchRooms(); // Refresh list
        }
      } else if (editingRoom) {
        const response = await updateSunbedRoom(editingRoom.id, { label });
        if (response.success || response.message) {
            fetchRooms(); // Refresh list
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save sunbed room", error);
      // Optional: Show error toast/message here
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
     if (deleteId) {
       try {
         await deleteSunbedRoom(deleteId);
         fetchRooms(); // Refresh list
       } catch (error) {
         console.error("Failed to delete room", error);
       }
       setDeleteId(null);
    }
    setIsDeleteOpen(false);
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        {/* Settings-style Header */}
        <SubPageHeader
          title="Sunbeds"
          subtitle="Manage your sunbed rooms, availability, and usage stats"
          icon={Sun}
          actions={
            <Button
              onClick={handleAdd}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-5 py-2.5 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus size={18} />
              Create New
            </Button>
          }
        />

        {/* Action Toolbar */}
        <SunbedActionToolbar
          totalItems={rooms.length}
          showingStart={paginatedRooms.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredRooms.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search rooms..."
        />

        {/* Table Section */}
        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <SunbedsTable
            rooms={paginatedRooms}
            indexOfFirstItem={indexOfFirstItem}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={loading}
          />

          {/* Pagination Footer */}
          {!loading && (
            <PaginationFooter
                totalItems={rooms.length}
                filteredTotal={filteredRooms.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      <AddSunbedModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        existingLabel={editingRoom?.label}
        mode={modalMode}
       />
       
       <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Room"
        message="Are you sure you want to delete this sunbed room?"
      />
    </div>
  );
};

export default SunbedsPage;
